import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader, SectionCard, Loading } from "@/components/common/Primitives";
import { DataTable } from "@/components/common/DataTable";
import { ReconcileDetailDrawer } from "@/components/mobilfunk/ReconcileDetailDrawer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtEUR } from "@/lib/format";

const STATUS = {
  uebereinstimmung: { label: "Übereinstimmung", cls: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20" },
  unterzahlung: { label: "Unterzahlung", cls: "bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/20" },
  ueberzahlung: { label: "Überzahlung", cls: "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/20" },
  fehlt: { label: "Fehlt", cls: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20" },
  pruefung: { label: "Prüfung", cls: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20" },
  offen: { label: "Offen", cls: "bg-muted text-muted-foreground border-border" },
};

function Kpi({ label, value, tone, testid }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid={testid}>
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-1.5 text-xl font-semibold ${tone || ""}`}>{value}</div>
    </div>
  );
}

export default function MfReports() {
  const [provider, setProvider] = useState("");
  const [period, setPeriod] = useState("");
  const [salesUser, setSalesUser] = useState("");
  const [detail, setDetail] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["mf-analytics", provider, period, salesUser],
    queryFn: async () => (await api.get(`/mobilfunk/analytics?provider_id=${provider}&period=${period}&sales_user_id=${salesUser}`)).data,
  });

  if (isLoading) return <Loading />;
  const d = data || {};
  const opts = d.filter_options || { providers: [], sales_users: [] };

  const drillCols = [
    { key: "customer", header: "Abschluss", render: (r) => (
      <div className="min-w-0"><div className="truncate font-medium">{r.customer_name}</div><div className="text-xs text-muted-foreground">{r.provider_name}</div></div>
    ) },
    { key: "soll", header: "Erwartet", render: (r) => fmtEUR(r.soll) },
    { key: "ist", header: "Abgerechnet", render: (r) => fmtEUR(r.ist) },
    { key: "diff", header: "Differenz", render: (r) => <span className={r.difference < 0 ? "text-red-600" : r.difference > 0 ? "text-sky-600" : ""}>{fmtEUR(r.difference)}</span> },
    { key: "status", header: "Status", render: (r) => <Badge className={STATUS[r.status]?.cls}>{STATUS[r.status]?.label || r.status}</Badge> },
  ];
  const provCols = [
    { key: "name", header: "Anbieter", render: (r) => r.name },
    { key: "count", header: "Verträge", render: (r) => r.count },
    { key: "expected", header: "Erwartet", render: (r) => fmtEUR(r.expected) },
    { key: "settled", header: "Abgerechnet", render: (r) => fmtEUR(r.settled) },
  ];
  const userCols = [
    { key: "name", header: "Mitarbeiter", render: (r) => r.name },
    { key: "count", header: "Verträge", render: (r) => r.count },
    { key: "expected", header: "Erwartet", render: (r) => fmtEUR(r.expected) },
  ];

  return (
    <div data-testid="mf-reports">
      <PageHeader title="Auswertungen" description="Erwartete, abgerechnete und bezahlte Provisionen – mit Drill-down auf die Abschlüsse." />

      <SectionCard className="mb-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="space-y-1.5"><Label>Anbieter</Label>
            <Select value={provider || "all"} onValueChange={(v) => setProvider(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="report-provider-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Alle</SelectItem>{opts.providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>Zeitraum (JJJJ-MM)</Label><Input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="z. B. 2026-04" data-testid="report-period-filter" /></div>
          <div className="space-y-1.5"><Label>Mitarbeiter</Label>
            <Select value={salesUser || "all"} onValueChange={(v) => setSalesUser(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="report-user-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Alle</SelectItem>{opts.sales_users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
      </SectionCard>

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Kpi label="Erwartet" value={fmtEUR(d.expected_commission)} testid="kpi-expected" />
        <Kpi label="Abgerechnet" value={fmtEUR(d.settled_commission)} testid="kpi-settled" />
        <Kpi label="Bezahlt (Gutschriften)" value={fmtEUR(d.paid_commission)} tone="text-emerald-600" testid="kpi-paid" />
        <Kpi label="Rückbelastungen" value={fmtEUR(d.chargebacks)} tone="text-red-600" testid="kpi-chargebacks" />
        <Kpi label="Offene Differenz" value={fmtEUR(d.open_difference)} tone={d.open_difference ? "text-amber-600" : ""} testid="kpi-difference" />
      </div>
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Verträge" value={d.contracts_count ?? 0} testid="kpi-contracts" />
        <Kpi label="In Prüfung" value={d.pruefung_count ?? 0} testid="kpi-pruefung" />
        <Kpi label="Nicht zugeordnet" value={fmtEUR(d.unmatched_amount)} testid="kpi-unmatched" />
        <Kpi label="Offene Reklamationen" value={d.reclamations_open ?? 0} testid="kpi-reclamations" />
      </div>

      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <SectionCard title="Nach Anbieter"><DataTable columns={provCols} rows={d.by_provider} testIdPrefix="prov-row" keyField="provider_id" /></SectionCard>
        <SectionCard title="Nach Mitarbeiter"><DataTable columns={userCols} rows={d.by_sales_user} testIdPrefix="user-row" keyField="sales_user_id" /></SectionCard>
      </div>

      <SectionCard title="Abschlüsse (Drill-down)">
        <DataTable columns={drillCols} rows={d.drilldown} testIdPrefix="drill-row" keyField="contract_id" onRowClick={(r) => setDetail(r.contract_id)} />
      </SectionCard>
      <ReconcileDetailDrawer contractId={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
